import React, { useState, useEffect } from 'react';
import { auditsApi } from '../services/api';

interface FileInfo {
  auditId: number;
  auditTitle: string;
  clientName: string;
  nombreOriginal: string;
  nombreArchivo: string;
  tamaño: number;
  tipoMime: string;
  fechaSubida: string;
}

const FileManagement: React.FC = () => {
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'date'>('date');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getAllFiles();
      if (response.data.success) {
        setFiles(response.data.data);
      }
    } catch (err: any) {
      setError('Error cargando archivos: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async (auditId: number, filename: string) => {
    const confirm = window.confirm(
      `¿Estás seguro de eliminar el archivo "${filename}"?\n\nEsta acción no se puede deshacer y solo libera espacio en disco.`
    );
    
    if (!confirm) return;

    try {
      setDeleting(filename);
      const response = await auditsApi.deleteFile(auditId, filename);
      if (response.data.success) {
        setSuccess(`Archivo "${filename}" eliminado exitosamente`);
        await loadFiles();
      }
    } catch (err: any) {
      setError('Error eliminando archivo: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(null);
    }
  };

  const downloadFile = async (auditId: number, filename: string) => {
    try {
      await auditsApi.downloadFile(auditId, filename);
    } catch (err: any) {
      setError('Error descargando archivo: ' + (err.message || 'Error desconocido'));
    }
  };

  // Función para obtener archivos de un mes específico
  const getFilesByMonth = (month: string) => {
    return files.filter(file => {
      const fileDate = new Date(file.fechaSubida);
      const fileMonth = `${fileDate.getFullYear()}-${String(fileDate.getMonth() + 1).padStart(2, '0')}`;
      return fileMonth === month;
    });
  };

  // Función para eliminar archivos de un mes específico
  const deleteFilesByMonth = async (month: string) => {
    const filesToDelete = getFilesByMonth(month);

    if (filesToDelete.length === 0) {
      setError('No hay archivos en el mes seleccionado');
      return;
    }

    const monthName = new Date(month + '-01').toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long'
    });

    const confirm = window.confirm(
      `¿Estás seguro de eliminar TODOS los archivos de ${monthName}?\n\n` +
      `Se eliminarán ${filesToDelete.length} archivos (${formatFileSize(filesToDelete.reduce((sum, f) => sum + f.tamaño, 0))}).\n\n` +
      `Esta acción NO SE PUEDE DESHACER.`
    );

    if (!confirm) return;

    try {
      setBulkDeleting(true);
      let deleted = 0;
      let errors = 0;

      for (const file of filesToDelete) {
        try {
          await auditsApi.deleteFile(file.auditId, file.nombreArchivo);
          deleted++;
        } catch (err) {
          errors++;
          console.error(`Error eliminando ${file.nombreOriginal}:`, err);
        }
      }

      if (errors === 0) {
        setSuccess(`✅ ${deleted} archivos de ${monthName} eliminados exitosamente`);
      } else {
        setError(`⚠️ ${deleted} archivos eliminados, ${errors} errores. Algunos archivos podrían no haberse eliminado.`);
      }

      await loadFiles();
    } catch (err: any) {
      setError('Error en eliminación masiva: ' + (err.response?.data?.error || err.message));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Función para obtener meses disponibles
  const getAvailableMonths = () => {
    const monthsSet = new Set<string>();
    files.forEach(file => {
      const fileDate = new Date(file.fechaSubida);
      const monthKey = `${fileDate.getFullYear()}-${String(fileDate.getMonth() + 1).padStart(2, '0')}`;
      monthsSet.add(monthKey);
    });

    return Array.from(monthsSet).sort().reverse(); // Más recientes primero
  };

  // Función para obtener el mes pasado
  const getLastMonth = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    return `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(file => 
    file.nombreOriginal.toLowerCase().includes(filter.toLowerCase()) ||
    file.clientName.toLowerCase().includes(filter.toLowerCase()) ||
    file.auditTitle.toLowerCase().includes(filter.toLowerCase())
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.nombreOriginal.localeCompare(b.nombreOriginal);
      case 'size':
        return b.tamaño - a.tamaño;
      case 'date':
        return new Date(b.fechaSubida).getTime() - new Date(a.fechaSubida).getTime();
      default:
        return 0;
    }
  });

  const totalSize = files.reduce((sum, file) => sum + file.tamaño, 0);
  const filteredSize = filteredFiles.reduce((sum, file) => sum + file.tamaño, 0);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>⏳ Cargando archivos...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: 0, color: '#495057' }}>📁 Gestión de Archivos</h2>
        <button
          onClick={loadFiles}
          disabled={loading}
          style={{
            background: '#28a745',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          🔄 Actualizar
        </button>
      </div>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          ❌ {error} (click para cerrar)
        </div>
      )}

      {success && (
        <div style={{
          background: '#d4edda',
          color: '#155724',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #c3e6cb',
          cursor: 'pointer'
        }} onClick={clearMessages}>
          ✅ {success} (click para cerrar)
        </div>
      )}

      {/* Estadísticas */}
      <div style={{
        background: '#f8f9fa',
        padding: '1rem',
        borderRadius: '6px',
        marginBottom: '1.5rem',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <strong>📊 Total de archivos:</strong> {files.length}
          </div>
          <div>
            <strong>💾 Espacio total:</strong> {formatFileSize(totalSize)}
          </div>
          <div>
            <strong>🔍 Archivos filtrados:</strong> {filteredFiles.length}
          </div>
          <div>
            <strong>📁 Espacio filtrado:</strong> {formatFileSize(filteredSize)}
          </div>
        </div>
      </div>

      {/* Controles de eliminación masiva */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '1.5rem'
      }}>
        <h4 style={{ margin: '0 0 1rem 0', color: '#856404' }}>
          🗑️ Eliminación Masiva de Archivos
        </h4>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontWeight: '500', color: '#856404' }}>
              Seleccionar mes:
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: '0.5rem',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                backgroundColor: 'white'
              }}
            >
              <option value="">-- Seleccionar mes --</option>
              {getAvailableMonths().map(month => {
                const monthName = new Date(month + '-01').toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long'
                });
                const fileCount = getFilesByMonth(month).length;
                const monthSize = formatFileSize(getFilesByMonth(month).reduce((sum, f) => sum + f.tamaño, 0));

                return (
                  <option key={month} value={month}>
                    {monthName} ({fileCount} archivos - {monthSize})
                  </option>
                );
              })}
            </select>
          </div>

          <button
            onClick={() => selectedMonth && deleteFilesByMonth(selectedMonth)}
            disabled={!selectedMonth || bulkDeleting}
            style={{
              background: selectedMonth ? '#dc3545' : '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: selectedMonth ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            {bulkDeleting ? '⏳ Eliminando...' : '🗑️ Eliminar archivos del mes'}
          </button>

          <button
            onClick={() => {
              const lastMonth = getLastMonth();
              if (getFilesByMonth(lastMonth).length > 0) {
                setSelectedMonth(lastMonth);
                deleteFilesByMonth(lastMonth);
              } else {
                setError('No hay archivos del mes pasado para eliminar');
              }
            }}
            disabled={bulkDeleting || getFilesByMonth(getLastMonth()).length === 0}
            style={{
              background: getFilesByMonth(getLastMonth()).length > 0 ? '#fd7e14' : '#6c757d',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '4px',
              cursor: getFilesByMonth(getLastMonth()).length > 0 ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            🚀 Eliminar mes pasado ({getFilesByMonth(getLastMonth()).length} archivos)
          </button>
        </div>

        <div style={{
          marginTop: '0.5rem',
          fontSize: '0.875rem',
          color: '#856404',
          fontStyle: 'italic'
        }}>
          ⚠️ La eliminación masiva es permanente y no se puede deshacer. Use con precaución.
        </div>
      </div>

      {/* Controles de filtrado */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="🔍 Buscar por nombre de archivo, cliente o auditoría..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <div>
          <label style={{ marginRight: '0.5rem', fontWeight: '500' }}>Ordenar por:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'size' | 'date')}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px'
            }}
          >
            <option value="date">📅 Fecha</option>
            <option value="name">📝 Nombre</option>
            <option value="size">📏 Tamaño</option>
          </select>
        </div>
      </div>

      {/* Lista de archivos */}
      {sortedFiles.length === 0 ? (
        <div style={{
          background: '#f8f9fa',
          padding: '2rem',
          textAlign: 'center',
          borderRadius: '6px',
          color: '#6c757d'
        }}>
          {filter ? '🔍 No se encontraron archivos con ese filtro' : '📭 No hay archivos subidos'}
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          {/* Header de tabla */}
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderBottom: '1px solid #dee2e6',
            display: 'grid',
            gridTemplateColumns: '1fr auto auto auto auto',
            gap: '1rem',
            alignItems: 'center',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            <div>📄 Archivo</div>
            <div>📏 Tamaño</div>
            <div>📅 Fecha</div>
            <div>⬇️ Descargar</div>
            <div>🗑️ Eliminar</div>
          </div>

          {/* Filas de archivos */}
          {sortedFiles.map((file, index) => (
            <div
              key={`${file.auditId}-${file.nombreArchivo}`}
              style={{
                padding: '1rem',
                borderBottom: index < sortedFiles.length - 1 ? '1px solid #f1f3f4' : 'none',
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto auto',
                gap: '1rem',
                alignItems: 'center',
                fontSize: '0.9rem'
              }}
            >
              <div>
                <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                  📄 {file.nombreOriginal}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  🏢 {file.clientName} - {file.auditTitle}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                  📋 Auditoría #{file.auditId}
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                {formatFileSize(file.tamaño)}
              </div>
              
              <div style={{ textAlign: 'right' }}>
                {new Date(file.fechaSubida).toLocaleDateString()}
              </div>
              
              <div>
                <button
                  onClick={() => downloadFile(file.auditId, file.nombreArchivo)}
                  style={{
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                  title="Descargar archivo"
                >
                  ⬇️
                </button>
              </div>
              
              <div>
                <button
                  onClick={() => deleteFile(file.auditId, file.nombreArchivo)}
                  disabled={deleting === file.nombreArchivo}
                  style={{
                    background: deleting === file.nombreArchivo ? '#6c757d' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    cursor: deleting === file.nombreArchivo ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem',
                    opacity: deleting === file.nombreArchivo ? 0.6 : 1
                  }}
                  title="Eliminar archivo (solo libera espacio)"
                >
                  {deleting === file.nombreArchivo ? '⏳' : '🗑️'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Nota informativa */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '6px',
        padding: '1rem',
        marginTop: '1.5rem',
        fontSize: '0.9rem',
        color: '#856404'
      }}>
        <strong>💡 Información:</strong>
        <ul style={{ margin: '0.5rem 0 0 1rem', paddingLeft: '1rem' }}>
          <li>Eliminar archivos solo libera espacio en disco</li>
          <li>No afecta el estado de las auditorías ni estados de cuenta</li>
          <li>Una vez eliminados, no se pueden recuperar</li>
          <li>Se recomienda descargar archivos importantes antes de eliminarlos</li>
        </ul>
      </div>
    </div>
  );
};

export default FileManagement;